import { RefreshCw } from 'lucide-react'

import { timeAgo } from '../lib/format'
import type { SyncStatus } from '../data/sync'

/**
 * Zeigt in einer Zeile, ob die Daten beim Server angekommen sind.
 *
 * Das ist unterwegs die wichtigste Auskunft der ganzen App: wer im Funkloch
 * einen Tagebucheintrag schreibt, will wissen, ob er nur auf dem Telefon liegt
 * oder schon gesichert ist.
 */
export function SyncBar({ status, onSync }: { status: SyncStatus; onSync: () => void }) {
  const { dot, text } = describe(status)

  return (
    <div className="status kein-druck" role="status" aria-live="polite">
      <span className={`status-punkt ${dot}`} aria-hidden="true" />
      <span>{text}</span>
      <button type="button" onClick={onSync} disabled={status.state === 'syncing'}>
        <RefreshCw size={12} className={status.state === 'syncing' ? 'dreht' : undefined} />{' '}
        Abgleichen
      </button>
    </div>
  )
}

function describe(status: SyncStatus): { dot: string; text: string } {
  const wartend =
    status.pending > 0
      ? `, ${status.pending} ${status.pending === 1 ? 'Änderung wartet' : 'Änderungen warten'}`
      : ''

  // Dauerhaft abgelehnte Fotos bleiben nur auf dem Gerät. Das muss dastehen,
  // sonst hält der Nutzer sie für gesichert und sie sind mit dem Gerät weg.
  const abgelehnt =
    status.failedUploads > 0
      ? status.failedUploads === 1
        ? ' — 1 Foto konnte der Server nicht annehmen, es bleibt nur auf diesem Gerät'
        : ` — ${status.failedUploads} Fotos konnte der Server nicht annehmen, sie bleiben nur auf diesem Gerät`
      : ''

  // Eine abgewiesene Änderung ist kein Fehler des Abgleichs - er ist geglückt.
  // Sie gehört trotzdem in die Zeile: der eigene Text ist dabei verschwunden,
  // und wer das nirgends liest, hält ihn für gespeichert.
  const abweisung = status.abweisung ? ` — ${status.abweisung}` : ''

  const { dot, text } = describeState(status, wartend)
  return {
    dot: dot || (abgelehnt || abweisung ? 'fehler' : ''),
    text: text + abgelehnt + abweisung,
  }
}

function describeState(status: SyncStatus, wartend: string): { dot: string; text: string } {
  switch (status.state) {
    case 'syncing':
      return { dot: '', text: 'Gleicht ab …' }
    case 'offline':
      return {
        dot: 'offline',
        text: `Offline — alles bleibt auf dem Gerät${wartend}`,
      }
    case 'signed-out':
      return { dot: 'fehler', text: 'Abgemeldet — neu anmelden, damit wieder abgeglichen wird' }
    case 'error':
      return {
        dot: 'fehler',
        text: `Abgleich fehlgeschlagen: ${status.message ?? 'unbekannter Fehler'}`,
      }
    default:
      return {
        dot: '',
        text: status.lastSync
          ? `Abgeglichen ${timeAgo(status.lastSync)}${wartend}`
          : `Noch nicht abgeglichen${wartend}`,
      }
  }
}
